import React, { useState, useEffect, useCallback, useRef } from 'react';
import { branding } from '../constants/branding';
import { FaShieldAlt, FaCog, FaFileDownload, FaHeadset, FaHistory, FaBell, FaChartLine, FaWallet, FaPlus, FaDice, FaExchangeAlt } from 'react-icons/fa';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { useLocation } from 'react-router-dom';
import axiosInstance from '../services/axiosConfig';
import { formatAmount } from '../utils/currency';
import TradingAccountModal from '../components/TradingAccountModal';
import IGamingAccountModal from '../components/IGamingAccountModal';
import { apiService } from '../services/apiService';

interface AccountData {
  id: number;
  type: string;
  name: string;
  balance: number;
  iban: string;
  bic: string;
  bank: string;
  currency: string;
  status: string;
  transactions: any[];
}

const Account: React.FC = () => {
  const [accounts, setAccounts] = useState<any[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Trading account states
  const [showTradingModal, setShowTradingModal] = useState(false);
  const [tradingWallet, setTradingWallet] = useState<any>(null);
  const [tradingWalletLoading, setTradingWalletLoading] = useState(false);

  // iGaming account states
  const [showIGamingModal, setShowIGamingModal] = useState(false);
  const [igamingAccount, setIGamingAccount] = useState<any>(null);
  const [igamingAccountLoading, setIGamingAccountLoading] = useState(false);
  const [igamingModalType, setIGamingModalType] = useState<'deposit' | 'withdraw'>('deposit');

  // Transfer states
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferFromAccount, setTransferFromAccount] = useState<string>('');
  const [transferToAccount, setTransferToAccount] = useState<string>('');
  const [transferAmount, setTransferAmount] = useState<string>('');
  const [transferDescription, setTransferDescription] = useState<string>('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState<string>('');
  const [transferSuccess, setTransferSuccess] = useState(false);

  const token = useAppSelector((state) => state.auth.token);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const profile = useAppSelector((state) => state.profile.profile);
  const authPermissions = useAppSelector((state: any) => state?.auth?.permissions || []);
  const dispatch = useAppDispatch();
  const location = useLocation();

  // Check if user has iGaming permission
  const userPermissions: string[] = (profile as any)?.permissions || authPermissions || [];
  const hasIGamingPermission = userPermissions.includes('VIEW_RETAIL_IGAMING');

  // Use refs to track if we're already fetching data
  const isFetching = useRef(false);
  const hasInitialized = useRef(false);
  const currentSelectedAccountId = useRef<string | null>(null);

  // Read accountId from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const accountIdFromUrl = urlParams.get('accountId');
    if (accountIdFromUrl) {
      setSelectedAccountId(accountIdFromUrl);
    }
  }, [location.search]);

  // Update ref when selectedAccountId changes
  useEffect(() => {
    currentSelectedAccountId.current = selectedAccountId;
  }, [selectedAccountId]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Consolidated data fetching function
  const fetchAllAccountData = useCallback(async () => {
    if (!token || !isAuthenticated || isFetching.current) {
      return;
    }

    isFetching.current = true;
    setLoading(true);
    setError(null);
    
    try {
      const response = await axiosInstance.get('/accounts');
      
      if (response.status === 200 && response.data) {
        setAccounts(response.data);
        // Set first account as selected if none selected and accounts exist
        if (response.data.length > 0 && !currentSelectedAccountId.current) {
          setSelectedAccountId(response.data[0].id.toString());
        }
      } else {
        setError('Failed to load accounts. Please try again.');
      }
    } catch (err: any) {
      console.error('Error fetching account data:', err);
      if (err.response?.status !== 401) {
        setError(err.message || 'An error occurred while fetching account data');
      }
    } finally {
      isFetching.current = false;
      setLoading(false);
    }
  }, [token, isAuthenticated]);

  // Fetch trading wallet data
  const fetchTradingWallet = useCallback(async () => {
    if (!profile?.id) return;
    
    setTradingWalletLoading(true);
    try {
      const response = await apiService.get(`/api/retail/trading/wallets/${profile.id}`);
      if (response.success) {
        setTradingWallet(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch trading wallet:', err);
      setTradingWallet(null);
    } finally {
      setTradingWalletLoading(false);
    }
  }, [profile?.id]);

  // Handle trading account creation success
  const handleTradingAccountSuccess = (walletData: any) => {
    setTradingWallet(walletData);
    setShowTradingModal(false);
  };

  // Fetch iGaming account
  const fetchIGamingAccount = useCallback(async () => {
    if (!profile?.id) return;
    
    setIGamingAccountLoading(true);
    try {
      const response = await apiService.get('/api/igaming-account');
      if (response.success) {
        setIGamingAccount(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch iGaming account:', err);
      setIGamingAccount(null);
    } finally {
      setIGamingAccountLoading(false);
    }
  }, [profile?.id]);

  // Handle iGaming account transaction success
  const handleIGamingAccountSuccess = () => {
    fetchIGamingAccount();
    fetchAllAccountData();
    setShowIGamingModal(false);
  };

  // Handle internal transfer
  const handleInternalTransfer = async () => {
    if (!transferFromAccount || !transferToAccount || !transferAmount) {
      setTransferError('Please fill in all required fields');
      return;
    }

    const amount = parseFloat(transferAmount);
    if (isNaN(amount) || amount <= 0) {
      setTransferError('Please enter a valid amount');
      return;
    }

    setTransferLoading(true);
    setTransferError('');
    setTransferSuccess(false);

    try {
      console.log('Sending transfer request:', {
        fromAccountNumber: transferFromAccount,
        toAccountNumber: transferToAccount,
        amount: amount
      });

      const response = await axiosInstance.post('/accounts/transfer', {
        fromAccountNumber: transferFromAccount,
        toAccountNumber: transferToAccount,
        amount: amount,
        description: transferDescription || `Transfer between accounts`
      });

      console.log('Transfer response:', response);

      if (response.status === 200) {
        setTransferSuccess(true);
        setTransferAmount('');
        setTransferDescription('');
        setTransferFromAccount('');
        setTransferToAccount('');
        
        // Refresh account data
        await fetchAllAccountData();
        
        // Close modal after 1.5 seconds
        setTimeout(() => {
          setShowTransferModal(false);
          setTransferSuccess(false);
        }, 1500);
      }
    } catch (err: any) {
      console.error('Transfer error:', err);
      console.error('Transfer error response:', err.response?.data);
      
      let errorMessage = 'Transfer failed. Please try again.';
      
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = 'Your session has expired. Please refresh the page and try again.';
        } else if (err.response.status === 403) {
          errorMessage = 'Access denied. You do not have permission to transfer from these accounts.';
        } else if (err.response.data) {
          if (err.response.data.errors && Array.isArray(err.response.data.errors) && err.response.data.errors.length > 0) {
            errorMessage = err.response.data.errors.join(', ');
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message;
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setTransferError(errorMessage);
    } finally {
      setTransferLoading(false);
    }
  };

  // Initialize data fetching only once when authenticated
  useEffect(() => {
    if (token && isAuthenticated && !hasInitialized.current) {
      hasInitialized.current = true;
      fetchAllAccountData();
    }
  }, [token, isAuthenticated, fetchAllAccountData]);

  // Fetch trading wallet and iGaming account when profile is available
  useEffect(() => {
    if (profile?.id) {
      fetchTradingWallet();
      // Only fetch iGaming account if user has permission
      if (hasIGamingPermission) {
        fetchIGamingAccount();
    }
    }
  }, [profile?.id, fetchTradingWallet, fetchIGamingAccount, hasIGamingPermission]);

  // Reset state when user logs out
  useEffect(() => {
    if (!token || !isAuthenticated) {
      hasInitialized.current = false;
      isFetching.current = false;
      setLoading(true);
      setError(null);
      setAccounts([]);
      setSelectedAccountId(null);
    }
  }, [token, isAuthenticated]);

  const selectedAccount = accounts.find(acc => acc.id.toString() === selectedAccountId);

  // --- SEPA LIMITS/USAGE LOGIC ---
  const sepaLimits = profile ? {
    daily: Number(profile.sepaDailyLimit ?? 0),
    monthly: Number(profile.sepaMonthlyLimit ?? 0),
    yearly: Number(profile.sepaYearlyLimit ?? 0),
    single: Number(profile.sepaSingleTransactionLimit ?? 0),
    currency: profile.sepaLimitCurrency ?? 'EUR',
  } : { daily: 0, monthly: 0, yearly: 0, single: 0, currency: 'EUR' };
  const sepaUsage = profile ? {
    daily: Number(profile.sepaDailyUsage ?? 0),
    monthly: Number(profile.sepaMonthlyUsage ?? 0),
    yearly: Number(profile.sepaYearlyUsage ?? 0),
  } : { daily: 0, monthly: 0, yearly: 0 };
  const over80 = (limit: number, usage: number) => limit > 0 && usage / limit >= 0.8;
  const show80Daily = over80(sepaLimits.daily, sepaUsage.daily);
  const show80Monthly = over80(sepaLimits.monthly, sepaUsage.monthly);
  const show80Yearly = over80(sepaLimits.yearly, sepaUsage.yearly);

  // Retry function for error state
  const handleRetry = () => {
    setError(null);
    hasInitialized.current = false;
    if (token && isAuthenticated) {
      fetchAllAccountData();
    }
  };

  // Get available months (previous months only)
  const getAvailableMonths = () => {
    const months = [];
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    // Start from previous month
    let month = currentMonth;
    let year = currentYear;

    // Get last 12 months, but only up to current month
    for (let i = 0; i < 12; i++) {
      if (month < 0) {
        month = 11;
        year--;
      }
      // Skip future months
      if (year < currentYear || (year === currentYear && month <= currentMonth)) {
        const monthName = new Date(year, month).toLocaleString('default', { month: 'long' });
        months.push({
          value: `${year}-${(month + 1).toString().padStart(2, '0')}`,
          label: `${monthName} ${year}`
        });
      }
      month--;
    }

    return months.sort((a, b) => b.value.localeCompare(a.value)); // Sort newest first
  };

  // Handle statement download
  const handleDownloadStatement = async () => {
    if (!selectedMonth || !selectedAccountId) return;

    setIsDownloading(true);
    try {
      const response = await axiosInstance.get(`/accounts/${selectedAccountId}/statement/${selectedMonth}`, {
        responseType: 'blob'
      });

      // Check if response is valid
      if (response.status === 200 && response.data) {
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        const fileName = `account-statement-${selectedMonth}.pdf`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        setShowStatementModal(false);
      } else {
        throw new Error('Could not download statement. Please try again.');
      }
    } catch (err: any) {
      console.error('Error downloading statement:', err);
      let errorMessage = 'Failed to download statement. Please try again.';
      
      if (err.response) {
        switch (err.response.status) {
          case 401:
            errorMessage = 'Your session has expired. Please log in again.';
            break;
          case 403:
            errorMessage = 'You do not have permission to download this statement.';
            break;
          case 404:
            errorMessage = 'Statement not found for the selected month.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
        }
      }
      
      setError(errorMessage);
      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsDownloading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        fontFamily: branding.fontFamily,
        background: branding.pageBackground,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, color: branding.textColor, marginBottom: 16 }}>Loading account data...</div>
          <div style={{ color: '#666' }}>Please wait while we fetch your account information</div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        fontFamily: branding.fontFamily,
        background: branding.pageBackground,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ fontSize: 18, color: branding.errorColor, marginBottom: 16 }}>Error Loading Account</div>
          <div style={{ color: '#666', marginBottom: 24 }}>{error}</div>
          <button 
            onClick={handleRetry} 
            style={{
              padding: '12px 24px',
              background: branding.primaryColor,
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show empty state if no accounts
  if (accounts.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        fontFamily: branding.fontFamily,
        background: branding.pageBackground,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ fontSize: 18, color: branding.textColor, marginBottom: 16 }}>No Accounts Found</div>
          <div style={{ color: '#666' }}>You don't have any accounts set up yet.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      fontFamily: branding.fontFamily,
      background: branding.pageBackground,
      padding: isMobile ? '24px 16px 120px' : '48px 24px'
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* SEPA Limits & Usage Panel */}
        {profile && sepaLimits.single > 0 && (
          <div style={{
            background: '#f7fafd',
            border: '1.5px solid #eee',
            borderRadius: 14,
            padding: 18,
            marginBottom: 24,
            marginTop: 4,
            boxShadow: '0 2px 8px rgba(10,132,255,0.04)',
            maxWidth: 480,
          }}>
            <div style={{ fontWeight: 700, color: branding.primaryColor, marginBottom: 8 }}>SEPA Transaction Limits</div>
            <div style={{ fontSize: 14, color: '#444', marginBottom: 4 }}>Single: {sepaLimits.currency} {sepaLimits.single.toLocaleString()} | Daily: {sepaLimits.currency} {sepaLimits.daily.toLocaleString()} | Monthly: {sepaLimits.currency} {sepaLimits.monthly.toLocaleString()} | Yearly: {sepaLimits.currency} {sepaLimits.yearly.toLocaleString()}</div>
            <div style={{ fontSize: 13, color: '#666' }}>
              Usage: Daily: {sepaLimits.currency} {sepaUsage.daily.toLocaleString()} | Monthly: {sepaLimits.currency} {sepaUsage.monthly.toLocaleString()} | Yearly: {sepaLimits.currency} {sepaUsage.yearly.toLocaleString()}
            </div>
            {(show80Daily || show80Monthly || show80Yearly) && (
              <div style={{ color: branding.warningColor || '#eab308', fontWeight: 600, marginTop: 8 }}>
                {show80Daily && `Warning: You are above 80% of your daily SEPA limit. `}
                {show80Monthly && `Warning: You are above 80% of your monthly SEPA limit. `}
                {show80Yearly && `Warning: You are above 80% of your yearly SEPA limit.`}
              </div>
            )}
          </div>
        )}
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 32,
        }}>
          <div>
            <h1 style={{ color: branding.textColor, fontWeight: 700, fontSize: isMobile ? 24 : 28, margin: 0 }}>Accounts</h1>
            <p style={{ color: '#666', fontSize: isMobile ? 14 : 16, margin: '8px 0 0' }}>Manage your bank accounts and settings</p>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => setShowAccountSettings(true)} style={headerButtonStyle}>
              <FaCog /> {!isMobile && 'Settings'}
            </button>
            <button onClick={() => setShowSecurityModal(true)} style={{...headerButtonStyle, background: branding.primaryColor, color: '#fff' }}>
              <FaShieldAlt /> {!isMobile && 'Security'}
            </button>
          </div>
        </div>

        {/* Main Content Grid */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 32 }}>
          {/* Left Column (Account List & Stats) */}
          <div style={{ flex: isMobile ? '1 1 auto' : '0 0 300px', display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Account List */}
            <div style={cardStyle}>
              <h3 style={cardHeaderStyle}>Your Accounts</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {(accounts || []).map(account => (
                  <div
                    key={account.id}
                    style={{
                      ...accountCardBaseStyle,
                      border: `2px solid ${selectedAccountId === account.id.toString() ? branding.primaryColor : 'transparent'}`,
                      background: selectedAccountId === account.id.toString() ? '#f7fafd' : '#fff',
                      position: 'relative'
                    }}
                  >
                    <div 
                      onClick={() => setSelectedAccountId(account.id.toString())}
                      style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, cursor: 'pointer' }}
                    >
                      <div style={{...iconStyle, background: account.type === 'Main Account' ? branding.panels.mainAccount.color : branding.panels.childWallet.color }}>
                        {account.type === 'Main Account' ? '🏦' : '👧'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: branding.textColor }}>{account.type}</div>
                        <div style={{ color: '#666', fontSize: 14 }}>Balance: {formatAmount(account.balance, account.currency)}</div>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Account number field (prioritize accountNumber, fallback to iban for backward compatibility)
                        const accountNumber = account.accountNumber || account.account_number || account.iban || account.id?.toString() || '';
                        setTransferFromAccount(accountNumber);
                        setShowTransferModal(true);
                        setTransferError('');
                        setTransferSuccess(false);
                      }}
                      style={{
                        padding: '6px 12px',
                        background: branding.primaryColor,
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        whiteSpace: 'nowrap'
                      }}
                      title="Transfer money from this account"
                    >
                      <FaExchangeAlt size={12} />
                      Transfer
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Trading Account */}
            <div style={cardStyle}>
              <h3 style={cardHeaderStyle}>Trading Account</h3>
              {tradingWalletLoading ? (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <div style={{ fontSize: 14, color: '#666' }}>Loading trading account...</div>
                </div>
              ) : tradingWallet && tradingWallet.hasTradingAccount ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{
                    ...accountCardBaseStyle,
                    border: '2px solid #10b981',
                    background: '#f0fdf4'
                  }}>
                    <div style={{...iconStyle, background: '#10b981' }}>
                      <FaChartLine style={{ color: 'white' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: branding.textColor }}>Trading Account</div>
                      <div style={{ color: '#666', fontSize: 14 }}>
                        Balance: {formatAmount(tradingWallet.totalBalance, tradingWallet.currency)}
                      </div>
                      <div style={{ color: '#10b981', fontSize: 12, marginTop: 2 }}>
                        Available: {formatAmount(tradingWallet.availableBalance, tradingWallet.currency)}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => setShowTradingModal(true)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid #10b981',
                        borderRadius: 6,
                        background: 'white',
                        color: '#10b981',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4
                      }}
                    >
                      <FaPlus /> Top Up
                    </button>
                    <button
                      onClick={() => window.location.href = '/trading-bots'}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: 'none',
                        borderRadius: 6,
                        background: '#10b981',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4
                      }}
                    >
                      <FaChartLine /> Trade
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <div style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
                    Create a trading account to start automated trading with bots and strategies.
                  </div>
                  <button
                    onClick={() => setShowTradingModal(true)}
                    style={{
                      padding: '12px 24px',
                      border: 'none',
                      borderRadius: 8,
                      background: branding.primaryColor,
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: 14,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      margin: '0 auto'
                    }}
                  >
                    <FaWallet /> Create Trading Account
                  </button>
                </div>
              )}
            </div>

            {/* iGaming Account - Only show if user has permission */}
            {hasIGamingPermission && (
            <div style={cardStyle}>
              <h3 style={cardHeaderStyle}>iGaming Account</h3>
              {igamingAccountLoading ? (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <div style={{ fontSize: 14, color: '#666' }}>Loading iGaming account...</div>
                </div>
              ) : igamingAccount && igamingAccount.id ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{
                    ...accountCardBaseStyle,
                    border: '2px solid #8b5cf6',
                    background: '#faf5ff'
                  }}>
                    <div style={{...iconStyle, background: '#8b5cf6' }}>
                      <FaDice style={{ color: 'white' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: branding.textColor }}>iGaming Account</div>
                      <div style={{ color: '#666', fontSize: 14 }}>
                        Balance: {formatAmount(igamingAccount.balance, igamingAccount.currency)}
                      </div>
                      <div style={{ color: '#8b5cf6', fontSize: 11, marginTop: 2 }}>
                        {igamingAccount.accountNumber}
                      </div>
                    </div>
                  </div>
                  
                  {/* Stats Grid */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: 8,
                    fontSize: 12,
                    color: '#666'
                  }}>
                    <div style={{ background: '#f9fafb', padding: '8px', borderRadius: 6 }}>
                      <div style={{ fontSize: 10, marginBottom: 2 }}>Total Deposited</div>
                      <div style={{ fontWeight: 600, color: '#10b981' }}>
                        {formatAmount(igamingAccount.totalDeposited, igamingAccount.currency)}
                      </div>
                    </div>
                    <div style={{ background: '#f9fafb', padding: '8px', borderRadius: 6 }}>
                      <div style={{ fontSize: 10, marginBottom: 2 }}>Total Withdrawn</div>
                      <div style={{ fontWeight: 600, color: '#ef4444' }}>
                        {formatAmount(igamingAccount.totalWithdrawn, igamingAccount.currency)}
                      </div>
                    </div>
                    <div style={{ background: '#f9fafb', padding: '8px', borderRadius: 6 }}>
                      <div style={{ fontSize: 10, marginBottom: 2 }}>Total Wagered</div>
                      <div style={{ fontWeight: 600, color: '#8b5cf6' }}>
                        {formatAmount(igamingAccount.totalWagered, igamingAccount.currency)}
                      </div>
                    </div>
                    <div style={{ background: '#f9fafb', padding: '8px', borderRadius: 6 }}>
                      <div style={{ fontSize: 10, marginBottom: 2 }}>Net Win/Loss</div>
                      <div style={{ fontWeight: 600, color: (igamingAccount.totalWon - igamingAccount.totalLost) >= 0 ? '#10b981' : '#ef4444' }}>
                        {formatAmount(igamingAccount.totalWon - igamingAccount.totalLost, igamingAccount.currency)}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => {
                        setIGamingModalType('deposit');
                        setShowIGamingModal(true);
                      }}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid #8b5cf6',
                        borderRadius: 6,
                        background: 'white',
                        color: '#8b5cf6',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4
                      }}
                    >
                      <FaPlus /> Deposit
                    </button>
                    <button
                      onClick={() => {
                        setIGamingModalType('withdraw');
                        setShowIGamingModal(true);
                      }}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: 'none',
                        borderRadius: 6,
                        background: '#8b5cf6',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4
                      }}
                    >
                      <FaDice /> Withdraw
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <div style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
                    Create an iGaming account to place bets on casino games and sports.
                  </div>
                  <button
                    onClick={() => {
                      setIGamingModalType('deposit');
                      setShowIGamingModal(true);
                    }}
                    style={{
                      padding: '12px 24px',
                      border: 'none',
                      borderRadius: 8,
                      background: branding.primaryColor,
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: 14,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      margin: '0 auto'
                    }}
                  >
                    <FaDice /> Create iGaming Account
                  </button>
                </div>
              )}
            </div>
            )}

            {/* Quick Actions */}
            <div style={cardStyle}>
              <h3 style={cardHeaderStyle}>Quick Actions</h3>
              <div style={{ display: 'grid', gap: 16, marginBottom: 32, gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                <button
                  onClick={() => setShowStatementModal(true)}
                  style={quickActionButtonStyle}
                >
                  <FaFileDownload size={20} color={branding.primaryColor} />
                  <div>
                    <div>Download Statement</div>
                    <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>Monthly account statements</div>
                  </div>
                </button>

                <button
                  style={quickActionButtonStyle}
                >
                  <FaHeadset size={20} color={branding.primaryColor} />
                  <div>
                    <div>Contact Support</div>
                    <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>Get help with your account</div>
                  </div>
                </button>

                <button
                  style={quickActionButtonStyle}
                >
                  <FaHistory size={20} color={branding.primaryColor} />
                  <div>
                    <div>Account History</div>
                    <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>View past transactions</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
          
          {/* Right Column (Details, Analytics, Transactions) */}
          <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: 24, minWidth: 0 }}>
            {selectedAccount && (
              <>
                {/* Account Details */}
                <div style={cardStyle}>
                  <h3 style={cardHeaderStyle}>Account Details for {selectedAccount.type}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px 24px' }}>
                    <DetailItem label="Account Holder" value={selectedAccount.name} />
                    <DetailItem label="IBAN" value={selectedAccount.iban} />
                    <DetailItem label="BIC" value={selectedAccount.bic} />
                    <DetailItem label="Bank" value={selectedAccount.bank} />
                    <DetailItem label="Currency" value={selectedAccount.currency} />
                    <DetailItem label="Status" value={<span style={{
                      fontWeight: 600,
                      color: branding.successColorText,
                      padding: '4px 12px',
                      background: branding.successColor,
                      borderRadius: 12,
                      fontSize: 12,
                    }}>{selectedAccount.status}</span>} />
                  </div>
                </div>

                {/* Recent Transactions */}
                <div style={cardStyle}>
                  <h3 style={cardHeaderStyle}>Recent Transactions</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #eee' }}>
                          <th style={tableHeaderStyle}>Description</th>
                          <th style={{...tableHeaderStyle, textAlign: 'right' }}>Amount</th>
                          <th style={{...tableHeaderStyle, textAlign: 'right' }}>Date</th>
                          <th style={{...tableHeaderStyle, textAlign: 'right' }}>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedAccount.transactions.map((tx: any) => (
                          <tr key={tx.id} style={{ borderBottom: '1px solid #f7f7f7' }}>
                            <td style={{ ...tableCellStyle, display: 'flex', alignItems: 'center', fontWeight: 500 }}>
                              <span style={{ ...iconStyle, width: 32, height: 32, fontSize: 16, background: tx.type === 'Credit' ? '#e8f5e8' : '#ffebee', color: tx.type === 'Credit' ? branding.successColor : branding.errorColor }}>
                                {tx.type === 'Credit' ? '↓' : '↑'}
                              </span>
                              {tx.description}
                            </td>
                            <td style={{...tableCellStyle, color: tx.type === 'Credit' ? branding.successColor : branding.errorColor, fontWeight: 600, textAlign: 'right' }}>
                              {tx.type === 'Credit' ? '+' : '-'}{formatAmount(Math.abs(tx.amount), selectedAccount.currency)}
                            </td>
                            <td style={{...tableCellStyle, color: '#666', textAlign: 'right' }}>{tx.transactionDate ? new Date(tx.transactionDate).toISOString().slice(0, 10) : ''}</td>
                            <td style={{...tableCellStyle, textAlign: 'right' }}>
                              <span style={{
                                padding: '4px 8px', borderRadius: 8, fontSize: 12, fontWeight: 500,
                                background: tx.type === 'Credit' ? '#e8f5e8' : '#ffebee',
                                color: tx.type === 'Credit' ? branding.successColor : branding.errorColor,
                              }}>
                                {tx.type}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Transaction Analytics */}
                <div style={cardStyle}>
                  <h3 style={cardHeaderStyle}>Transaction Categories</h3>
                  <div style={{ display: 'grid', gap: 16 }}>
                    {selectedAccount.transactionCategories && selectedAccount.transactionCategories.map((cat: any, index: number) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 12, height: 12, borderRadius: '50%', background: cat.color }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontWeight: 500, color: branding.textColor }}>{cat.category}</span>
                            <span style={{ fontWeight: 600, color: branding.textColor }}>${formatAmount(cat.amount, selectedAccount.currency)}</span>
                          </div>
                          <div style={{ width: '100%', height: 4, background: '#eee', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ width: `${cat.percentage}%`, height: '100%', background: cat.color, borderRadius: 2 }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Trading Account Modal */}
      {profile?.id && (
        <TradingAccountModal
          isOpen={showTradingModal}
          onClose={() => setShowTradingModal(false)}
          onSuccess={handleTradingAccountSuccess}
          userId={profile.id}
        />
      )}

      {/* iGaming Account Modal - Only show if user has permission */}
      {hasIGamingPermission && showIGamingModal && (
        <IGamingAccountModal
          isOpen={showIGamingModal}
          onClose={() => setShowIGamingModal(false)}
          onSuccess={handleIGamingAccountSuccess}
          type={igamingModalType}
          accounts={accounts}
          igamingAccount={igamingAccount}
        />
      )}

      {/* Security Modal */}
      {showSecurityModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={modalHeaderStyle}>Account Security</h3>
            <div style={{ display: 'grid', gap: 16, marginBottom: 32 }}>
              <SecurityToggle label="Two-Factor Authentication" description="Enhanced account security" enabled={true} />
              <SecurityToggle label="Login Notifications" description="Get notified of new logins" enabled={true} />
              <SecurityToggle label="Transaction Alerts" description="Real-time transaction notifications" enabled={false} />
            </div>
            <button onClick={() => setShowSecurityModal(false)} style={{ ...headerButtonStyle, background: branding.primaryColor, color: '#fff', width: '100%' }}>
              Save Settings
            </button>
          </div>
        </div>
      )}

      {/* Account Settings Modal */}
      {showAccountSettings && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={modalHeaderStyle}>Account Settings</h3>
            <div style={{ display: 'grid', gap: 16, marginBottom: 32 }}>
              <SecurityToggle label="Email Notifications" description="Receive updates via email" enabled={true} />
              <SecurityToggle label="SMS Notifications" description="Receive updates via SMS" enabled={false} />
              <SecurityToggle label="Paper Statements" description="Receive paper statements" enabled={false} />
            </div>
            <button onClick={() => setShowAccountSettings(false)} style={{ ...headerButtonStyle, background: branding.primaryColor, color: '#fff', width: '100%' }}>
              Save Settings
            </button>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={modalHeaderStyle}>Transfer Between Accounts</h3>
            
            {transferError && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                background: '#ffebee',
                color: branding.errorColor,
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                {transferError}
              </div>
            )}

            {transferSuccess && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                background: '#e8f5e8',
                color: branding.successColorText || '#10b981',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600
              }}>
                Transfer completed successfully!
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 8, color: '#666', fontSize: 14, fontWeight: 600 }}>
                  From Account
                </label>
                <select
                  value={transferFromAccount}
                  onChange={(e) => setTransferFromAccount(e.target.value)}
                  disabled={transferLoading}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1.5px solid #eee',
                    fontSize: '16px',
                    color: branding.textColor,
                    background: '#fff'
                  }}
                >
                  <option value="">Select account</option>
                  {accounts.filter(acc => (acc.accountNumber || acc.account_number || acc.iban || acc.id)).map(account => {
                    // Prioritize accountNumber, fallback to iban for backward compatibility
                    const accountNumber = account.accountNumber || account.account_number || account.iban || account.id?.toString() || '';
                    return (
                      <option key={account.id} value={accountNumber}>
                        {account.type || account.accountType || 'Account'} - {formatAmount(account.balance || 0, account.currency || 'USD')}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 8, color: '#666', fontSize: 14, fontWeight: 600 }}>
                  To Account
                </label>
                <select
                  value={transferToAccount}
                  onChange={(e) => setTransferToAccount(e.target.value)}
                  disabled={transferLoading}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1.5px solid #eee',
                    fontSize: '16px',
                    color: branding.textColor,
                    background: '#fff'
                  }}
                >
                  <option value="">Select account</option>
                  {accounts
                    .filter(acc => {
                      // Prioritize accountNumber, fallback to iban for backward compatibility
                      const accNumber = acc.accountNumber || acc.account_number || acc.iban || acc.id?.toString() || '';
                      return accNumber !== transferFromAccount;
                    })
                    .map(account => {
                      // Prioritize accountNumber, fallback to iban for backward compatibility
                      const accountNumber = account.accountNumber || account.account_number || account.iban || account.id?.toString() || '';
                      return (
                        <option key={account.id} value={accountNumber}>
                          {account.type || account.accountType || 'Account'} - {formatAmount(account.balance || 0, account.currency || 'USD')}
                        </option>
                      );
                    })}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 8, color: '#666', fontSize: 14, fontWeight: 600 }}>
                  Amount
                </label>
                <input
                  type="number"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  disabled={transferLoading}
                  placeholder="0.00"
                  min="0.01"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1.5px solid #eee',
                    fontSize: '16px',
                    color: branding.textColor,
                    background: '#fff'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 8, color: '#666', fontSize: 14, fontWeight: 600 }}>
                  Description (Optional)
                </label>
                <input
                  type="text"
                  value={transferDescription}
                  onChange={(e) => setTransferDescription(e.target.value)}
                  disabled={transferLoading}
                  placeholder="Transfer between accounts"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1.5px solid #eee',
                    fontSize: '16px',
                    color: branding.textColor,
                    background: '#fff'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    setTransferError('');
                    setTransferSuccess(false);
                    setTransferAmount('');
                    setTransferDescription('');
                    setTransferFromAccount('');
                    setTransferToAccount('');
                  }}
                  disabled={transferLoading}
                  style={{
                    ...headerButtonStyle,
                    background: '#fff',
                    color: branding.textColor,
                    border: '1.5px solid #eee',
                    opacity: transferLoading ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleInternalTransfer}
                  disabled={!transferFromAccount || !transferToAccount || !transferAmount || transferLoading}
                  style={{
                    ...headerButtonStyle,
                    background: branding.primaryColor,
                    color: '#fff',
                    opacity: (!transferFromAccount || !transferToAccount || !transferAmount || transferLoading) ? 0.6 : 1,
                    cursor: (!transferFromAccount || !transferToAccount || !transferAmount || transferLoading) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  {transferLoading ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #ffffff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      <span>Transferring...</span>
                    </>
                  ) : (
                    <>
                      <FaExchangeAlt size={16} />
                      <span>Transfer</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statement Download Modal */}
      {showStatementModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={modalHeaderStyle}>Download Statement</h3>
            
            {error && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                background: '#ffebee',
                color: branding.errorColor,
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}
            
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', marginBottom: 8, color: '#666', fontSize: 14 }}>
                Select Month
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1.5px solid #eee',
                  fontSize: '16px',
                  color: branding.textColor,
                  background: '#fff'
                }}
              >
                <option value="">Select a month</option>
                {getAvailableMonths().map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowStatementModal(false);
                  setError(null);
                }}
                style={{
                  ...headerButtonStyle,
                  background: '#fff',
                  color: branding.textColor,
                  border: '1.5px solid #eee'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDownloadStatement}
                disabled={!selectedMonth || isDownloading}
                style={{
                  ...headerButtonStyle,
                  background: branding.primaryColor,
                  color: '#fff',
                  opacity: (!selectedMonth || isDownloading) ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                {isDownloading ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <span>Downloading...</span>
                  </>
                ) : (
                  <>
                    <FaFileDownload size={16} />
                    <span>Download</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailItem: React.FC<{label: string; value: React.ReactNode}> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>{label}</div>
    <div style={{ fontWeight: 600, color: branding.textColor }}>{value}</div>
  </div>
);

const SecurityToggle: React.FC<{label: string, description: string, enabled: boolean}> = ({ label, description, enabled }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#f7fafd', borderRadius: 12 }}>
      <div>
        <div style={{ fontWeight: 600, color: branding.textColor }}>{label}</div>
        <div style={{ fontSize: 14, color: '#666' }}>{description}</div>
      </div>
      <div style={{ width: 48, height: 24, background: enabled ? branding.primaryColor : '#ccc', borderRadius: 12, position: 'relative', cursor: 'pointer' }}>
        <div style={{ width: 20, height: 20, background: '#fff', borderRadius: '50%', position: 'absolute', top: 2, transition: 'all 0.2s', [enabled ? 'right' : 'left']: 2 }} />
      </div>
    </div>
);

// Common Styles
const cardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 20,
  padding: 24,
  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
};

const cardHeaderStyle: React.CSSProperties = {
  margin: '0 0 20px 0',
  color: branding.textColor,
  fontSize: 18,
  fontWeight: 600,
};

const headerButtonStyle: React.CSSProperties = {
  padding: '12px 24px',
  background: '#fff',
  color: branding.primaryColor,
  border: '1.5px solid #eee',
  borderRadius: 12,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  transition: 'all 0.2s'
};

const accountCardBaseStyle: React.CSSProperties = {
  padding: 16,
  borderRadius: 14,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  transition: 'all 0.2s',
  border: '2px solid transparent'
};

const iconStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 20,
  color: '#fff',
};

const quickActionButtonStyle: React.CSSProperties = {
  padding: '12px 16px',
  background: 'transparent',
  border: '1.5px solid #eee',
  borderRadius: 12,
  fontSize: 15,
  fontWeight: 600,
  color: branding.textColor,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  transition: 'all 0.2s',
  textAlign: 'left'
};

const tableHeaderStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  color: '#666',
  whiteSpace: 'nowrap',
  fontSize: 14
};

const tableCellStyle: React.CSSProperties = {
  padding: '12px 16px',
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: '24px',
};

const modalContentStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 24, padding: 40,
  maxWidth: 480, width: '100%',
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
};

const modalHeaderStyle: React.CSSProperties = {
  margin: '0 0 24px 0',
  color: branding.textColor,
  fontSize: 24,
  fontWeight: 700,
  textAlign: 'center',
};

export default Account; 